import { Route, Routes, useNavigate } from 'react-router-dom';
import { GamePage } from './screens/GamePage';
import { WelcomePage } from './screens/WelcomePage';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { SeasonInfo } from './screens/SeasonInfo';
import { DailyRewardsPage } from './screens/DailyRewardsPage';
import { LeaderBoardPage } from './screens/LeaderBoardPage';
import { SeasonLeaderboard } from './screens/SeasonLeaderboard';
import { SeasonsList } from './screens/SeasonsList';
import { InviteFriends } from './screens/InviteFriends';
import { Quests } from './screens/Quests';
import styled from 'styled-components';
import { bonusGameApi } from 'src/api/bonus-game';
import { NotDepositedModal } from './screens/GamePage/components/NotDepositedModal';
import { useLoading } from 'src/hooks/useLoading';
import ScrollToTop from 'src/components/ScrollToTop';
import { QuestDetailPage } from './screens/QuestDetail';
import { EditTree } from './screens/EditTree';
import { useUserContext } from './contexts/UserContext';
import { Promo } from './screens/Promo';

const Layout = styled.div`
  display: flex;
  justify-content: center;
  & > div {
    width: 100%;
  }
  height: 100%;
  min-height: 100vh;
  padding: 0 0 20px;
`;

const rootPath = '/bonus-game';

export const BonusGameRoutes = () => {
  const [isGameAvailable, setGameAvailable] = useState(false);
  const [randomBonusModalVisible, setRandomBonusModalVisible] = useState(false);
  const [isSeasonOver, setSeasonOver] = useState(true);
  const [loadingLocal, setLoadingLocal] = useState(true);
  const { setGameStat } = useUserContext();

  const [resetDate, isResetDateLoading] = useLoading(async () => {
    const resetDateRes = await bonusGameApi.resetDate();
    const resetDate = Number(resetDateRes.result);

    const currentTime = Date.now();

    if (currentTime > Number(resetDate)) {
      setSeasonOver(true);
    } else {
      setSeasonOver(false);
    }
  });

  const [checkAvailability, isGameAvailabilityLoading] = useLoading(
    async () => {
      const isAvailableResponse = await bonusGameApi.checkAvailability();
      setGameAvailable(isAvailableResponse.result);
      setLoadingLocal(false);
    },
  );

  const [getStats, isStatLoading] = useLoading(async () => {
    const getStatRes = await bonusGameApi.getStat();
    setGameStat(getStatRes.result);
    setRandomBonusModalVisible(getStatRes.result.randomRewardAvailable);
  });

  useEffect(() => {
    if (isGameAvailable) {
      getStats();
    }
  }, [isGameAvailable]);

  useEffect(() => {
    resetDate();
    checkAvailability();
  }, []);

  const isLoading =
    isResetDateLoading ||
    isGameAvailabilityLoading ||
    isStatLoading ||
    loadingLocal;

  if (isLoading) return null;

  if (!isGameAvailable) {
    return <NotDepositedModal />;
  }

  return (
    <Layout>
      <ScrollToTop />

      {isSeasonOver ? (
        <Routes>
          <Route
            path={`${rootPath}`}
            element={<LeaderBoardPage isSeasonOver={isSeasonOver} />}
          />
        </Routes>
      ) : (
        <Routes>
          <Route
            path={rootPath}
            element={
              <GamePage
                randomBonusModalVisible={randomBonusModalVisible}
                setRandomBonusModalVisible={setRandomBonusModalVisible}
              />
            }
          />
          <Route path={`${rootPath}/welcome`} element={<WelcomePage />} />
          <Route path={`${rootPath}/promo`} element={<Promo />} />
          <Route path={`${rootPath}/season-info`} element={<SeasonInfo />} />
          <Route
            path={`${rootPath}/current-season`}
            element={<SeasonLeaderboard />}
          />
          <Route path={`${rootPath}/seasons`} element={<SeasonsList />} />
          <Route
            path={`${rootPath}/season/:id`}
            element={<SeasonLeaderboard />}
          />
          <Route path={`${rootPath}/invite`} element={<InviteFriends />} />
          <Route path={`${rootPath}/quests`} element={<Quests />} />
          <Route
            path={`${rootPath}/quests-details`}
            element={<QuestDetailPage />}
          />
          <Route
            path={`${rootPath}/daily-rewards`}
            element={<DailyRewardsPage />}
          />
          <Route
            path={`${rootPath}/top-leaderboard`}
            element={<LeaderBoardPage isSeasonOver={isSeasonOver} />}
          />
          <Route path={`${rootPath}/edit-tree`} element={<EditTree />} />
        </Routes>
      )}
    </Layout>
  );
};
